import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Role, RolePrivilege } from '../models/role.model';

/**
 * RoleManagementService - USCF-069
 *
 * Service used to interact with role and role-privilege API endpoints.
 * - /api/role: CRUD operations for roles
 * - /api/roleprivilege: Read and update role privileges by module
 */
@Injectable({
  providedIn: 'root',
})
export class RoleManagementService {
  private readonly apiUrl = environment.apiUrl.replace(/\/$/, '');
  private readonly roleBase = `${this.apiUrl}/api/role`;
  private readonly privilegeBase = `${this.apiUrl}/api/roleprivilege`;

  constructor(private readonly http: HttpClient) {}

  /** Gets all system roles. */
  getAllRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.roleBase}/all`);
  }

  /** Gets a role by ID. */
  getRoleById(id: string): Observable<Role> {
    return this.http.get<Role>(`${this.roleBase}/by-id/${id}`);
  }

  /**
   * Creates a new role.
   * The backend expects multipart/form-data with a "name" field.
   */
  createRole(name: string): Observable<Role> {
    const formData = new FormData();
    formData.append('name', name);
    return this.http.post<Role>(`${this.roleBase}/create`, formData);
  }

  /** Updates el nombre de un role existente. */
  updateRole(role: Role): Observable<Role> {
    return this.http.put<Role>(`${this.roleBase}/update`, role);
  }

  /** Deletes un role por su ID. */
  deleteRole(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.roleBase}/${id}`);
  }

  /** Gets todos los privilegios de un role especifico. */
  getPrivilegesByRole(roleId: string): Observable<RolePrivilege[]> {
    return this.http.get<RolePrivilege[]>(`${this.privilegeBase}/by-role/${roleId}`);
  }

  /** Updates un privilegio de role. */
  updatePrivilege(privilege: RolePrivilege): Observable<RolePrivilege> {
    return this.http.put<RolePrivilege>(`${this.privilegeBase}/update`, privilege);
  }
}
